// apps/web/src/lib/estoque/consumo-tinta.ts — Fase 13, Plano 32 (EST-14, EST-15, D-01..D-04, D-06).
// Apresentação pura da tela "Consumo de tinta": variação com sinal, fonte do consumo, fórmula do real, linha de rateio,
// situação do dia de rateio e filtros (URL e query da API). Nada aqui recalcula consumo: quem soma, rateia e filtra é o
// servidor (13-28 e 13-31); a tela só apresenta.
import type { FonteConsumoTinta, ModoPesagem, RateioDiaLinha } from '@pieltec/contracts';
import { formatarData } from '../vendas/formatacao';
import { formatarQuantidadeEstoque } from './formatacao';

const TRACO = '—';
/** Sinal de menos tipográfico (U+2212): o mesmo do UI-SPEC ("−0,120 kg"). */
const MENOS = '−';

const FORMATO_VARIACAO = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 4 });
const FORMATO_KG = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

// ---------------------------------------------------------------------------------------------------------------------
// Variação previsto x real
// ---------------------------------------------------------------------------------------------------------------------

/** Variação sempre com sinal e texto ("+0,300 kg" / "−0,120 kg" / "0 kg"); sem previsto ou sem real é traço. */
export function textoVariacao(kg: number | null | undefined): string {
  if (kg === null || kg === undefined || Number.isNaN(kg)) return TRACO;
  if (kg === 0) return '0 kg';
  const modulo = FORMATO_VARIACAO.format(Math.abs(kg));
  return `${kg > 0 ? '+' : MENOS}${modulo} kg`;
}

/** Acima do previsto em âmbar; igual ou abaixo em texto normal. A cor nunca é o único sinal: o texto leva o sinal. */
export function classeVariacao(kg: number | null | undefined): string {
  return kg !== null && kg !== undefined && kg > 0 ? 'text-amber-700' : '';
}

// ---------------------------------------------------------------------------------------------------------------------
// Fonte do consumo (D-04, R13-12)
// ---------------------------------------------------------------------------------------------------------------------

export type IconeFonteConsumo = 'Scale' | 'Divide' | 'ClipboardList' | 'FileClock';

export interface ApresentacaoFonte {
  readonly rotulo: string;
  /** Ícones do UI-SPEC (a página mapeia o nome para o componente do lucide-react). */
  readonly icones: readonly IconeFonteConsumo[];
  /** Classes do badge (família de cor da paleta "Fonte do consumo"). */
  readonly classe: string;
  /** Explicação curta da origem do número. */
  readonly detalhe: string;
}

const CLASSE_EMERALD = 'border-emerald-200 bg-emerald-50 text-emerald-700';
const CLASSE_SLATE = 'border-slate-200 bg-slate-50 text-slate-700';
const CLASSE_AMBER = 'border-amber-200 bg-amber-50 text-amber-700';

const FONTES: Readonly<Record<FonteConsumoTinta, ApresentacaoFonte>> = {
  PESAGEM: {
    rotulo: 'Pesagem',
    icones: ['Scale'],
    classe: CLASSE_EMERALD,
    detalhe: 'Saídas pesadas pelo almoxarifado, menos as devoluções.',
  },
  RATEIO: {
    rotulo: 'Rateio por área',
    icones: ['Divide'],
    classe: CLASSE_SLATE,
    detalhe: 'Consumo do dia distribuído entre os lotes pela área pintada.',
  },
  MISTA: {
    rotulo: 'Mista — Pesagem + Rateio',
    icones: ['Scale', 'Divide'],
    classe: CLASSE_EMERALD,
    detalhe: 'Parte pesada no lote (Pesagem) e parte rateada por área do dia (Rateio por área).',
  },
  APONTAMENTO: {
    rotulo: 'Apontamento',
    icones: ['ClipboardList'],
    classe: CLASSE_SLATE,
    detalhe: 'Consumo informado no apontamento da produção.',
  },
  ESTIMADO: {
    rotulo: 'Consumo estimado',
    icones: ['Scale'],
    classe: CLASSE_AMBER,
    detalhe: 'Estimado pelo insumo previsto do roteiro, sem pesagem nem rateio.',
  },
  PENDENTE: {
    rotulo: 'Pendente',
    icones: ['FileClock'],
    classe: CLASSE_AMBER,
    detalhe: 'Ainda não há consumo efetivo: o rateio do dia está aberto ou só houve perda.',
  },
};

/** Rótulo, ícones e cor da fonte do consumo; fonte desconhecida (API mais nova) cai em "Pendente", nunca quebra a linha. */
export function apresentacaoFonte(fonte: FonteConsumoTinta): ApresentacaoFonte {
  return FONTES[fonte] ?? FONTES.PENDENTE;
}

export const TEXTO_RATEIO_PENDENTE = 'Rateio pendente';

// ---------------------------------------------------------------------------------------------------------------------
// Linha expandida: fórmula do real e movimentos (D-01, D-02)
// ---------------------------------------------------------------------------------------------------------------------

/** "Real = saídas {S} kg − devoluções {D} kg" (líquidos de estorno, como o servidor soma). */
export function textoFormulaReal(saidasKg: number, devolucoesKg: number): string {
  return `Real = saídas ${FORMATO_KG.format(saidasKg)} kg ${MENOS} devoluções ${FORMATO_KG.format(devolucoesKg)} kg`;
}

const ROTULOS_MODO: Readonly<Record<ModoPesagem, string>> = { SAIDA: 'Saída', DEVOLUCAO: 'Devolução', PERDA: 'Perda' };

/** Tipo do movimento de tinta como o UI-SPEC o escreve. */
export function rotuloModoPesagem(modo: ModoPesagem): string {
  return ROTULOS_MODO[modo] ?? modo;
}

export interface EntradaLinhaRateio {
  readonly consumoKg: number;
  /** YYYY-MM-DD */
  readonly dia: string;
  readonly areaM2: number;
  readonly kgPorM2: number | null;
}

const FORMATO_KG_POR_M2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 6 });

/** "{qtd} kg rateados do dia dd/MM/yyyy ({m²} m² × {kg/m²} kg/m²)"; sem kg/m² (dia sem área) mostra só a área. */
export function textoLinhaRateio(entrada: EntradaLinhaRateio): string {
  const base = `${FORMATO_KG.format(entrada.consumoKg)} kg rateados do dia ${formatarData(entrada.dia)}`;
  const area = formatarQuantidadeEstoque(entrada.areaM2, 'm²');
  if (entrada.kgPorM2 === null) return `${base} (${area})`;
  return `${base} (${area} × ${FORMATO_KG_POR_M2.format(entrada.kgPorM2)} kg/m²)`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Rateio diário (D-03)
// ---------------------------------------------------------------------------------------------------------------------

export type EstadoRateio = 'fechado' | 'aberto' | 'pendente';

/**
 * Situação do dia de rateio. Fechado é fechado; aberto de um dia anterior a hoje é "pendente" (o consumo ainda não chegou
 * ao rascunho fiscal); aberto de hoje ainda está em andamento. `hojeIso` é o dia de negócio (America/Sao_Paulo).
 */
export function estadoRateio(linha: Pick<RateioDiaLinha, 'situacao' | 'dia'>, hojeIso: string): EstadoRateio {
  if (linha.situacao === 'FECHADO') return 'fechado';
  return linha.dia < hojeIso ? 'pendente' : 'aberto';
}

/** Só o dia anterior a hoje, ainda aberto e com entrega, pode ser fechado (o servidor repete a checagem). */
export function podeFecharRateio(linha: Pick<RateioDiaLinha, 'situacao' | 'dia' | 'entregueKg'>, hojeIso: string): boolean {
  return linha.situacao === 'ABERTO' && linha.dia < hojeIso && linha.entregueKg > 0;
}

/** Primeira frase da confirmação do fechamento (cor, data, lotes); o aviso de irreversibilidade fica no diálogo. */
export function textoConfirmacaoFechamento(entrada: { cor: string; dia: string; lotes: number }): string {
  return `O consumo de ${entrada.cor} em ${formatarData(entrada.dia)} será distribuído entre ${entrada.lotes} lotes e ficará disponível para o rascunho fiscal.`;
}

export const MENSAGEM_ERRO_GENERICO = 'Não foi possível concluir a operação. Tente novamente.';
export const MENSAGEM_SEM_PERMISSAO = 'Você não tem permissão para esta ação.';

const MENSAGENS_RATEIO: Readonly<Record<string, string>> = {
  RATEIO_JA_FECHADO: 'O rateio deste dia já foi fechado. Atualize a lista para ver o resultado.',
  RATEIO_SEM_LOTES: 'Nenhum lote foi pintado com esta cor neste dia. Não há para onde distribuir o consumo.',
  RATEIO_SEM_ENTREGA: 'Nenhuma tinta entregue nesta cor neste dia.',
  RATEIO_DIA_EM_ANDAMENTO: 'O dia ainda não terminou. O rateio só pode ser fechado a partir do dia seguinte.',
  TINTA_NAO_ALTO_GIRO: 'Esta tinta não está marcada como "Alto giro, sem devolução".',
};

/**
 * Mensagem do erro ao fechar o rateio. Os códigos 422/409 do servidor (13-28) viram texto claro; o corpo é o JSON do
 * Nest (`{ codigo, message }`). 403 e demais falhas caem nas mensagens genéricas do UI-SPEC, sem vazar detalhe técnico.
 */
export function mensagemErroRateio(status: number, corpo: unknown): string {
  if (status === 403) return MENSAGEM_SEM_PERMISSAO;
  const codigo = corpo !== null && typeof corpo === 'object' ? (corpo as { codigo?: unknown }).codigo : undefined;
  if (typeof codigo === 'string' && MENSAGENS_RATEIO[codigo]) return MENSAGENS_RATEIO[codigo];
  return MENSAGEM_ERRO_GENERICO;
}

// ---------------------------------------------------------------------------------------------------------------------
// Filtros do relatório "Por OP e lote"
// ---------------------------------------------------------------------------------------------------------------------

export type AbaConsumoTinta = 'lotes' | 'rateio';

/** Aba ativa de `location.search` (`?aba=rateio`); qualquer outro valor é "Por OP e lote". */
export function abaConsumoDaUrl(search: string): AbaConsumoTinta {
  return new URLSearchParams(search).get('aba') === 'rateio' ? 'rateio' : 'lotes';
}

export interface FiltrosConsumoTinta {
  /** YYYY-MM-DD ou vazio (a API assume os últimos 30 dias). */
  de: string;
  ate: string;
  /** UUID do cliente; vazio = todos. */
  clienteId: string;
  tinta: string;
  busca: string;
  soComPerda: boolean;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DIA = /^\d{4}-\d{2}-\d{2}$/;
const LIMITE_TEXTO = 60;
export const TAMANHO_PAGINA_CONSUMO = 50;

export function filtrosConsumoVazios(): FiltrosConsumoTinta {
  return { de: '', ate: '', clienteId: '', tinta: '', busca: '', soComPerda: false };
}

export function filtrosConsumoAtivos(filtros: FiltrosConsumoTinta): boolean {
  return (
    filtros.de !== '' ||
    filtros.ate !== '' ||
    filtros.clienteId !== '' ||
    filtros.tinta.trim() !== '' ||
    filtros.busca.trim() !== '' ||
    filtros.soComPerda
  );
}

/** Período válido: sem datas, uma só data ou De <= Até. */
export function periodoConsumoValido(filtros: Pick<FiltrosConsumoTinta, 'de' | 'ate'>): boolean {
  return filtros.de === '' || filtros.ate === '' || filtros.de <= filtros.ate;
}

/** Query string (sem `?`) só com os filtros ativos; `URLSearchParams` escapa qualquer valor digitado. */
export function montarQueryConsumoTinta(filtros: FiltrosConsumoTinta, opcoes: { pagina?: number; tamanhoPagina?: number } = {}): string {
  const params = new URLSearchParams();
  params.set('pagina', String(opcoes.pagina ?? 1));
  params.set('tamanhoPagina', String(opcoes.tamanhoPagina ?? TAMANHO_PAGINA_CONSUMO));
  if (DIA.test(filtros.de)) params.set('de', filtros.de);
  if (DIA.test(filtros.ate)) params.set('ate', filtros.ate);
  if (filtros.clienteId !== '') params.set('clienteId', filtros.clienteId);
  const tinta = filtros.tinta.trim();
  if (tinta !== '') params.set('tinta', tinta.slice(0, LIMITE_TEXTO));
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca.slice(0, LIMITE_TEXTO));
  if (filtros.soComPerda) params.set('soComPerda', 'true');
  return params.toString();
}

/** Filtros a partir de `location.search`; data ou UUID inválidos voltam ao padrão em vez de gerar 400. */
export function lerFiltrosConsumoDaUrl(search: string): FiltrosConsumoTinta {
  const params = new URLSearchParams(search);
  const filtros = filtrosConsumoVazios();
  const de = params.get('de') ?? '';
  const ate = params.get('ate') ?? '';
  filtros.de = DIA.test(de) ? de : '';
  filtros.ate = DIA.test(ate) ? ate : '';
  const cliente = params.get('cliente') ?? '';
  filtros.clienteId = UUID.test(cliente) ? cliente : '';
  filtros.tinta = (params.get('tinta') ?? '').trim().slice(0, LIMITE_TEXTO);
  filtros.busca = (params.get('busca') ?? '').trim().slice(0, LIMITE_TEXTO);
  filtros.soComPerda = params.get('perda') === '1';
  return filtros;
}

/** `?aba=...&...` para `history.replaceState`: a aba ativa sempre vai na URL, os filtros só quando preenchidos. */
export function escreverConsumoNaUrl(aba: AbaConsumoTinta, filtros: FiltrosConsumoTinta): string {
  const params = new URLSearchParams();
  params.set('aba', aba);
  if (aba === 'lotes') {
    if (filtros.de !== '') params.set('de', filtros.de);
    if (filtros.ate !== '') params.set('ate', filtros.ate);
    if (filtros.clienteId !== '') params.set('cliente', filtros.clienteId);
    if (filtros.tinta.trim() !== '') params.set('tinta', filtros.tinta.trim());
    if (filtros.busca.trim() !== '') params.set('busca', filtros.busca.trim());
    if (filtros.soComPerda) params.set('perda', '1');
  }
  return `?${params.toString()}`;
}
